import {
  getURLConfigs,
  getStandardErrorObject,
  getTransactionId,
  logError,
  getLoggingHeaders,
  fetchWithTimeout,
  urlHelper,
} from './Utils.js';
import { ClientErrors } from './ClientErrors';
import { GUEST_SIGNUP_TIMEOUT_MS } from './Constants';
import uuid from 'uuid';

const DEGRADED_MODE_ERROR_CODE = 'UNAUTHORIZED_TOKEN_DEGRADED_MODE';

/**
 * Guest signup returns 401 UNAUTHORIZED_TOKEN_DEGRADED_MODE when the token
 * service is degraded. In that case send the user to the otpLogin page with the
 * redirectUri instead of failing silently.
 * @returns {Promise<boolean>} true if the page is being redirected
 */
function redirectToOtpLoginIfDegraded(result, redirectUri) {
  if (result.status !== 401) {
    return Promise.resolve(false);
  }
  return result
    .clone()
    .json()
    .then((data) => {
      const isDegraded = data?.Error?.details?.some(
        (detail) => detail?.errorCode === DEGRADED_MODE_ERROR_CODE,
      );
      if (!isDegraded) {
        return false;
      }
      window.location.href = urlHelper(
        `${getURLConfigs().BASE_URL_SSO}/otpLogin`,
        redirectUri ? { redirectUri } : undefined,
      );
      return true;
    })
    .catch(() => false);
}

export function doGuestLoginApiCall(redirectUri) {
  return new Promise((resolve, reject) => {
    const body = {
      globalInfo: {
        core: {
          primaryEmail: uuid(),
        },
        metadata: {},
      },
    };
    fetchWithTimeout(
      `${getURLConfigs().BASE_URL_GUEST_API}/v3/guest/account/signup`,
      {
        credentials: 'include',
        method: 'post',
        headers: {
          'content-type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
          ...getLoggingHeaders(),
        },
        body: JSON.stringify(body),
      },
      { retry: false, timeoutMs: GUEST_SIGNUP_TIMEOUT_MS },
    )
      .then((result) => {
        if (result.status === 201) {
          result
            .json()
            .then((data) => {
              const bounceForm = document.createElement('form');
              bounceForm.setAttribute('method', 'POST');
              document.body.appendChild(bounceForm);
              bounceForm.setAttribute(
                'action',
                `${getURLConfigs().BASE_URL_GUEST_API}/v3/sso/brand/SFLY/sync`,
              );

              const bounceFormRedirectUri = document.createElement('input');
              bounceFormRedirectUri.setAttribute('type', 'hidden');
              bounceFormRedirectUri.setAttribute('name', 'redirectUri');
              bounceFormRedirectUri.setAttribute('value', redirectUri);
              bounceForm.appendChild(bounceFormRedirectUri);

              const bounceFormRefreshToken = document.createElement('input');
              bounceFormRefreshToken.setAttribute('type', 'hidden');
              bounceFormRefreshToken.setAttribute('name', 'refreshToken');
              bounceFormRefreshToken.setAttribute('value', data.refresh_token);
              //bounceFormRefreshToken.setAttribute('value', refreshToken);
              bounceForm.appendChild(bounceFormRefreshToken);

              // Set clockDrift for all cases
              const bounceFormClockDrift = document.createElement('input');
              bounceFormClockDrift.setAttribute('type', 'hidden');
              bounceFormClockDrift.setAttribute('name', 'clockDrift');
              bounceFormClockDrift.setAttribute('value', 0);
              bounceForm.appendChild(bounceFormClockDrift);

              const bounceFormMaxAgeInSeconds = document.createElement('input');
              bounceFormMaxAgeInSeconds.setAttribute('type', 'hidden');
              bounceFormMaxAgeInSeconds.setAttribute('name', 'maxAgeInSeconds');
              bounceFormMaxAgeInSeconds.setAttribute('value', 38880000);
              bounceForm.appendChild(bounceFormMaxAgeInSeconds);

              // Submit the form to post to the server
              bounceForm.submit();
            })
            .catch((e) => {
              // A 201 with an unparseable body would otherwise leave the promise
              // pending forever; fail it so callers don't hang.
              const transId = getTransactionId();
              logError(
                ClientErrors.GUEST_LOGIN_FAILED,
                {
                  status: result.status,
                  name: e?.name,
                  message: e?.message,
                  stack: e?.stack,
                },
                transId,
              );
              reject(
                getStandardErrorObject(
                  ClientErrors.GUEST_LOGIN_FAILED,
                  transId,
                ),
              );
            });
        }
        if (result.status !== 201) {
          const transId = getTransactionId();
          // Response's status/statusText/url are prototype getters, not own
          // properties, so passing `result` itself (or spreading it) loses them -
          // pull the fields out explicitly.
          logError(
            ClientErrors.GUEST_LOGIN_FAILED,
            {
              status: result.status,
              statusText: result.statusText,
              url: result.url,
            },
            transId,
          );
          if (result.status === 401) {
            redirectToOtpLoginIfDegraded(result, redirectUri).then(
              (redirected) => {
                if (!redirected) {
                  resolve();
                }
              },
            );
          } else if (result.status === 424) {
            result
              .json()
              .then((data) => {
                //Cognito down so redirecting to login page.
                if (data.error === 'AUTH_PROVIDER_UNAVAILABLE') {
                  window.location.assign(getURLConfigs().BASE_URL_SSO);
                } else {
                  resolve();
                }
              })
              .catch((e) => {
                // A 424 with an unparseable body would otherwise leave the promise
                // pending forever; fail it so callers don't hang.
                logError(
                  ClientErrors.GUEST_LOGIN_FAILED,
                  {
                    status: result.status,
                    name: e?.name,
                    message: e?.message,
                    stack: e?.stack,
                  },
                  transId,
                );
                reject(
                  getStandardErrorObject(
                    ClientErrors.GUEST_LOGIN_FAILED,
                    transId,
                  ),
                );
              });
          } else {
            resolve();
          }
        }
      })
      .catch((e) => {
        console.log(e);
        // Get a transId to link errors from the client to splunk
        const transId = getTransactionId();
        logError(
          ClientErrors.GUEST_LOGIN_FAILED,
          {
            name: e?.name,
            message: e?.message,
            stack: e?.stack,
            ...(e.response ?? {}),
          },
          transId,
        );
        reject(
          getStandardErrorObject(ClientErrors.GUEST_LOGIN_FAILED, transId),
        );
      });
  });
}

/**
 * Creates a guest account via /v3/guest/account/signup only - unlike
 * doGuestLoginApiCall(), it never builds/submits the bounce form to
 * /v3/sso/brand/SFLY/sync, so it never navigates the page. Callers that want
 * the resulting session picked up in-page should follow a successful resolve
 * with resyncTokens() instead.
 *
 * On a 401 UNAUTHORIZED_TOKEN_DEGRADED_MODE the page is redirected to otpLogin
 * with the optional redirectUri (falls back to the current page).
 */
export function createGuestSession(redirectUri) {
  return new Promise((resolve, reject) => {
    const body = {
      globalInfo: {
        core: {
          primaryEmail: uuid(),
        },
        metadata: {},
      },
    };
    fetchWithTimeout(
      `${getURLConfigs().BASE_URL_GUEST_API}/v3/guest/account/signup`,
      {
        credentials: 'include',
        method: 'post',
        headers: {
          'content-type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
          ...getLoggingHeaders(),
        },
        body: JSON.stringify(body),
      },
      { retry: false, timeoutMs: GUEST_SIGNUP_TIMEOUT_MS },
    )
      .then((result) => {
        if (result.status === 201) {
          resolve();
          return;
        }
        const transId = getTransactionId();
        // Response's status/statusText/url are prototype getters, not own
        // properties, so passing `result` itself (or spreading it) loses them -
        // pull the fields out explicitly.
        logError(
          ClientErrors.GUEST_LOGIN_FAILED,
          {
            status: result.status,
            statusText: result.statusText,
            url: result.url,
          },
          transId,
        );
        redirectToOtpLoginIfDegraded(result, redirectUri).then((redirected) => {
          if (!redirected) {
            reject(
              getStandardErrorObject(ClientErrors.GUEST_LOGIN_FAILED, transId),
            );
          }
        });
      })
      .catch((e) => {
        const transId = getTransactionId();
        logError(
          ClientErrors.GUEST_LOGIN_FAILED,
          {
            name: e?.name,
            message: e?.message,
            stack: e?.stack,
          },
          transId,
        );
        reject(
          getStandardErrorObject(ClientErrors.GUEST_LOGIN_FAILED, transId),
        );
      });
  });
}
